import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge, Grid } from "@chakra-ui/react";
import { FaBuilding, FaHome, FaHotel, FaWarehouse, FaStore, FaTree, FaLayerGroup, FaGlobe } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyStrategyDiversification: React.FC = () => {
  const propertyTypes = [
    { icon: FaHome, name: "Residential", desc: "Houses & apartments", color: "blue", allocation: "40%" },
    { icon: FaBuilding, name: "Commercial", desc: "Office & retail", color: "purple", allocation: "25%" },
    { icon: FaWarehouse, name: "Industrial", desc: "Warehouses & factories", color: "orange", allocation: "15%" },
    { icon: FaHotel, name: "Hospitality", desc: "Hotels & short-term", color: "green", allocation: "10%" },
    { icon: FaStore, name: "Retail", desc: "Shopping centers", color: "red", allocation: "5%" },
    { icon: FaTree, name: "Land", desc: "Development sites", color: "teal", allocation: "5%" }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategy/diversification"
        fallbackTitle="Portfolio Diversification"
        fallbackDescription="Build a diversified property investment portfolio"
      />
      <NavbarWithCallToAction />

      <Box bg="linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" color="white" py={20}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", md: "3fr 2fr" }} gap={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Badge bg="purple.400" px={4} py={2} borderRadius="full">Reduce Super Reliance</Badge>
              <Heading size="3xl" fontWeight="black">Build Wealth Outside the Sharemarket</Heading>
              <Text fontSize="xl" color="whiteAlpha.900" lineHeight="1.8">
                Your super is exposed to market volatility. Property provides a tangible asset that doesn't swing with share prices—giving you stability and security for retirement.
              </Text>
            </VStack>
            <Box bg="whiteAlpha.200" p={8} borderRadius="2xl" textAlign="center">
              <Icon as={FaLayerGroup} boxSize={16} mb={4} />
              <Text fontSize="2xl" fontWeight="bold">6+ Asset Classes</Text>
            </Box>
          </Grid>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Diversification Options</Heading>
          <Text fontSize="lg" color="gray.600">Different property types offer different benefits—we help you choose based on your retirement goals and risk tolerance</Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {propertyTypes.map((type) => (
            <Box key={type.name} bg={`${type.color}.50`} p={8} borderRadius="2xl" border="2px solid" borderColor={`${type.color}.200`} textAlign="center">
              <Icon as={type.icon} boxSize={12} color={`${type.color}.600`} mb={4} />
              <Heading size="md" mb={2}>{type.name}</Heading>
              <Text fontSize="sm" color="gray.600" mb={4}>{type.desc}</Text>
              <Badge colorScheme={type.color} fontSize="lg" px={4} py={2}>{type.allocation}</Badge>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl">Geographic Diversification</Heading>
            <Text fontSize="lg" color="gray.600">Don't put all your eggs in one basket—spreading across markets reduces risk and captures growth wherever it occurs</Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Icon as={FaGlobe} boxSize={10} color="blue.500" mb={4} />
              <Heading size="md" mb={3}>Metropolitan</Heading>
              <Text color="gray.600" fontSize="sm">Capital cities - steady growth & liquidity</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Icon as={FaGlobe} boxSize={10} color="green.500" mb={4} />
              <Heading size="md" mb={3}>Regional</Heading>
              <Text color="gray.600" fontSize="sm">High-growth areas with infrastructure</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" boxShadow="md">
              <Icon as={FaGlobe} boxSize={10} color="purple.500" mb={4} />
              <Heading size="md" mb={3}>Interstate</Heading>
              <Text color="gray.600" fontSize="sm">Cross-state exposure for stability</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box bg="purple.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaLayerGroup} boxSize={16} />
            <Heading size="xl">Reduce Your Reliance on Super</Heading>
            <Text fontSize="lg" maxW="2xl">Property gives you control over your retirement wealth. Book a consultation to design a portfolio that complements your superannuation strategy.</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategyDiversification;
